import { EntityType } from '@/components/entities/entityType';
import SettingsValidator from '@/components/entities/settingsValidators';
import Dagre from '@dagrejs/dagre';
import { Edge, MarkerType, Node, NodeProps, NodeTypes } from '@xyflow/react';
import { nanoid } from 'nanoid';
import AgentNode from './agentNode';
import DataSourceNode from './dataSourceNode';
import UserAppNode from './userAppNode';

export type NodeData = {
  currentEntityId?: string;
  entity?: any;
  name?: string;
  entityNode?: boolean;
  panelMaxHieght: number;
  partition?: string;
  selected?: boolean;
  viewOnly?: boolean;
  settingsValidator?: SettingsValidator;
  updateNode?: (entity: any) => void;
  deleteNode?: (nodeId: string) => void;
  openPropertiesPanel?: (nodeId: string) => void;
  getNewEntities?: () => any[];
  getAllEntities?: () => any[];
};

export type NodeType = EntityType.dataSourceInstance | EntityType.agentInstance | EntityType.userAppInstance;

export type DataSourceNodeType = Node<NodeData, EntityType.dataSourceInstance>;
export type AgentNodeType = Node<NodeData, EntityType.agentInstance>;
export type UserAppNodeType = Node<NodeData, EntityType.userAppInstance>;

export type AppNodeType = DataSourceNodeType | AgentNodeType | UserAppNodeType;

export const nodeTypesMap: NodeTypes = {
  [EntityType.dataSourceInstance]: DataSourceNode,
  [EntityType.agentInstance]: AgentNode,
  [EntityType.userAppInstance]: UserAppNode,
};

export const createEntityNode = async (
  existingNodes: Node<NodeData>[],
  entity: any,
  panelMaxHieght: number,
  viewOnly: boolean,
): Promise<Node<NodeData>> => {
  if (!entity.nodeId) {
    entity.nodeId = entity.id || nanoid();
  }

  const nodeType = getNodeType(entity);
  let entityNode = existingNodes.find((n) => n.id === entity.nodeId);
  if (!entityNode) {
    entityNode = createNewNode(entity.nodeId, nodeType, { entity, entityNode: true, panelMaxHieght, viewOnly });
  } else {
    entityNode.data.entity = entity;
    entityNode.data.viewOnly = viewOnly;
  }

  return entityNode;
};

export const getNodeType = (entity: any) => {
  if (entity.entityType === EntityType.userAppInstance) {
    return nodeTypesMap[entity.userAppDefId] ? entity.userAppDefId : entity.entityType || EntityType.userAppInstance;
  } else if (entity.entityType === EntityType.dataSourceInstance) {
    return nodeTypesMap[entity.dataSourceDefId]
      ? entity.dataSourceDefId
      : entity.entityType || EntityType.dataSourceInstance;
  } else if (entity.entityType === EntityType.agentInstance) {
    return nodeTypesMap[entity.agentDefId] ? entity.agentDefId : entity.entityType || EntityType.agentInstance;
  }
};

export const createNewNode = (nodeId: string, nodeType: NodeType, nodeData: NodeData): Node<NodeData> => {
  return { id: nodeId, type: nodeType, data: nodeData, position: { x: 0, y: 0 } };
};

export const createEventEdge = (eventSubscription: any, color: string): Edge => {
  const { eventSourceId, eventSubscriberId, eventName } = eventSubscription;

  const edge: Edge = {
    id: `${eventSourceId}_${eventSubscriberId}_${eventName}`,
    // type: deletable ? 'deletableEdge' : '',
    type: 'smoothstep',
    source: eventSourceId,
    target: eventSubscriberId,
    style: { stroke: color },
    data: { eventSubscription },
    label: eventName,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 10,
      height: 10,
      color: color,
    },
    animated: true,
  };
  return edge;
};

export const createSourceEntityEdge = (sourceEntity: any, color: string): Edge => {
  const { sourceEntityId, targetEntityId } = sourceEntity;

  const edge: Edge = {
    id: `${sourceEntityId}_${targetEntityId}`,
    // type: deletable ? 'deletableEdge' : '',
    source: sourceEntityId,
    target: targetEntityId,
    style: { stroke: color, strokeDasharray: '5 5' },
    data: { sourceEntity },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 10,
      height: 10,
      color: color,
    },
  };
  return edge;
};

export const createDelegationEdge = (
  supervisorId: string,
  workerId: string,
  label: string = 'Delegates',
  color: string = '#9333ea',
): Edge => {
  const edge: Edge = {
    id: `delegation_${supervisorId}_${workerId}`,
    type: 'smoothstep',
    source: supervisorId,
    target: workerId,
    style: { stroke: color, strokeDasharray: '4 4', strokeWidth: 2 },
    data: { isDelegation: true },
    label: label,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 12,
      height: 12,
      color: color,
    },
    animated: true,
  };
  return edge;
};

export const getLayoutedElements = (nodes: Node<NodeData>[], edges: Edge[], rankdir: 'LR' | 'TB' = 'LR') => {
  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: rankdir, ranksep: 150, align: 'UL', ranker: 'tight-tree' });

  edges.forEach((edge) => g.setEdge(edge.source, edge.target, { label: edge.label?.toString() || '', labelpos: 'c' }));
  nodes.forEach((node) =>
    g.setNode(node.id, { ...node, width: node.measured?.width ?? 0, height: node.measured?.height ?? 0 }),
  );

  Dagre.layout(g);

  return {
    nodes: nodes.map((node) => {
      const position = g.node(node.id);
      // We are shifting the dagre node position (anchor=center center) to the top left
      // so it matches the React Flow node anchor point (top left).
      const x = position.x - (node.measured?.width ?? 0) / 2;
      const y = position.y - (node.measured?.height ?? 0) / 2;

      return { ...node, position: { x, y } };
    }),
    edges,
  };
};
